import { type JSX, useCallback, useMemo, useRef } from 'react';

import { type Namespace, useTranslation } from '@cx-modules/i18n-provider';

import { useOddsGridContext } from '../../context/OddsGridContext';
import { closeGridToolTipCookie } from '../../helpers';
import { useTeamNickNameFromBet } from '../../hooks/useTeamNickNameFromBet';
import { useSelector } from '../../providers/StoreProvider';
import type { Bet, OnOddsButtonClick } from '../../types';
import { categoryIdToCategoryMap } from '../../utils/labels';
import { BestOddsButton } from '../BestOddsButton';
import { Chevron } from '../Chevron';
import { NewUserTooltip } from '../NewUserTooltip';
import { SeeMoreOddsTip } from '../SeeMoreOddsTip';

import { BetBestOddsButtonWrapper, BetColumnTopBetName, BetContent, BetNameWrapper, SeeAllOdds } from './style';

interface BetColumnTopProps {
  bestOddBet?: Bet & { bestOdds: number; bestOddsFractional: string; bestOddsText: string | number };
  betId: number;
  disableStreamer: boolean;
  isOpen: boolean;
  marketId: number;
  onOddsButtonClick?: OnOddsButtonClick;
  renderBadge: boolean;
  showCompareOddsToolTip: boolean;
  showNewUserTooltip?: boolean;
  toggle: () => void;
}

export const BetColumnTop = ({
  bestOddBet,
  betId,
  disableStreamer,
  isOpen,
  marketId,
  onOddsButtonClick,
  renderBadge,
  showCompareOddsToolTip,
  showNewUserTooltip,
  toggle,
}: BetColumnTopProps): JSX.Element => {
  const { oddsShown } = useOddsGridContext();
  const setSeeMoreOddsTooltip = useSelector(state => state.setSeeMoreOddsTooltip);
  const { categoryGroupId, seeMoreOdds } = useSelector(state => ({
    categoryGroupId: state.categoryGroupId,
    seeMoreOdds: state.seeMoreOddsTooltip || false,
  }));
  const ns = categoryIdToCategoryMap?.[categoryGroupId];
  const { t } = useTranslation(['label', ns as Namespace]);
  const teamNickName = useTeamNickNameFromBet(bestOddBet?.genericName ?? '', t, ns);
  const translatedBestOddBetNameFallback = bestOddBet?.name ? t(bestOddBet?.name, { ns }) : '';
  const numberOfOdds = bestOddBet?.odds.filter(odd => odd.status.toLowerCase() === 'active').length;
  const bestOddsText = bestOddBet?.bestOddsText ?? '';

  const bestOdd = bestOddBet?.odds.find(
    odd => odd.decimal === bestOddBet?.bestOdds && odd.status.toLowerCase() === 'active',
  );
  const bookieCode = bestOdd?.bookmakerCode ?? '';

  const steamOrDrift = bestOdd?.movement?.toLowerCase() || '';

  const buttonWrapperRef = useRef<HTMLDivElement>(null);

  const seeAllOddsLabel = t('CX_ODDS_GRID_SEE_ALL_ODDS', {
    ns: 'label',
  });

  const handleCompareAllOddsClick = useCallback(() => {
    setSeeMoreOddsTooltip(false);
    toggle();
    closeGridToolTipCookie();
  }, [setSeeMoreOddsTooltip, toggle]);

  const gridTipVisible = useMemo(() => {
    return seeMoreOdds && showCompareOddsToolTip;
  }, [seeMoreOdds, showCompareOddsToolTip]);

  const renderGridToggleWithWrapper = (gridToggler: JSX.Element) => {
    if (!numberOfOdds) return null;

    return gridTipVisible ? (
      <SeeMoreOddsTip givenPlacement="bottom" isVisible={false}>
        {gridToggler}
      </SeeMoreOddsTip>
    ) : (
      gridToggler
    );
  };

  const betColumnTopName = `${teamNickName || translatedBestOddBetNameFallback} ${bestOddBet?.line?.name || ''}`;

  return (
    <BetContent data-testid="market-bet" isOpen={isOpen}>
      <BetNameWrapper onClick={handleCompareAllOddsClick}>
        <BetColumnTopBetName data-testid="bet-name" ref={buttonWrapperRef}>
          {betColumnTopName}
        </BetColumnTopBetName>
        {renderGridToggleWithWrapper(
          <SeeAllOdds aria-expanded={isOpen}>
            <span>
              {seeAllOddsLabel} ({numberOfOdds})
            </span>
            <Chevron expanded={isOpen} />
          </SeeAllOdds>,
        )}
      </BetNameWrapper>
      <BetBestOddsButtonWrapper>
        <BestOddsButton
          bestOddsText={bestOddsText}
          betId={betId}
          bookieCode={bookieCode}
          disableStreamer={disableStreamer}
          marketId={marketId}
          oddsShown={oddsShown}
          onOddsButtonClick={onOddsButtonClick}
          renderBadge={renderBadge}
          steamOrDrift={steamOrDrift}
        />
      </BetBestOddsButtonWrapper>
      {showNewUserTooltip && <NewUserTooltip buttonRef={buttonWrapperRef} orientation="right" />}
    </BetContent>
  );
};
